import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/SEO';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const siteUrl = import.meta.env.VITE_SITE_URL || '';

const INQUIRY_LABELS: Record<string, string> = {
  product_inquiry: 'Product Inquiry',
  distributor: 'Distributor Partnership',
  bulk_order: 'Bulk Order',
  veterinary_advice: 'Veterinary Advice',
  other: 'Other',
};

const WHATSAPP_NUMBER = '923007321011';

const contactJsonLd = {
  '@type': 'ContactPage',
  name: 'Contact Hamva Pharmaceuticals',
  url: `${siteUrl}/contact`,
  description: 'Get in touch with Hamva Pharmaceuticals for veterinary product inquiries, distributor partnerships, and animal health advice.',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+92-300-7321011',
    contactType: 'customer service',
    availableLanguage: ['en', 'ur'],
    areaServed: 'PK',
    contactOption: 'WhatsApp',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '8km Lahore GT Road Rawat',
    addressLocality: 'Rawalpindi',
    addressCountry: 'PK',
  },
};

export default function Contact() {
  const { ref, isVisible } = useScrollAnimation();
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;

    const form = e.currentTarget;
    const formData = new FormData(form);
    const name = String(formData.get('name') || '').trim();
    const phone = String(formData.get('phone') || '').trim();
    const email = String(formData.get('email') || '').trim();
    const inquiryType = String(formData.get('inquiry_type') || 'product_inquiry');
    const message = String(formData.get('message') || '').trim();

    if (!name || !phone || !message) return;

    const inquiryLabel = INQUIRY_LABELS[inquiryType] || inquiryType;
    const whatsappMessage = `Hello Hamva Pharmaceuticals,\n\nNew Inquiry from Website:\n\nFull Name: ${name}\nPhone / WhatsApp: ${phone}\nEmail: ${email || 'Not provided'}\nInquiry Type: ${inquiryLabel}\nMessage: ${message}\n\nPlease contact me regarding this inquiry.`;

    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    setFormStatus('sending');
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
      setFormStatus('success');
      form.reset();
      setCharCount(0);
    }, 400);
  };

  return (
    <div className="min-h-screen bg-dark font-inter text-white">
      <SEO
        title="Contact Us | Hamva Pharmaceuticals - WhatsApp &amp; Inquiry"
        description="Get in touch with Hamva Pharmaceuticals for veterinary product inquiries, distributor partnerships, and bulk orders. WhatsApp +92 300 7321011."
        keywords="contact hamva pharmaceuticals, veterinary products inquiry, animal health distributor, livestock supplements order"
        ogTitle="Contact Hamva Pharmaceuticals"
        ogDescription="Reach out for veterinary product inquiries, partnerships, and personalized recommendations."
        ogImage="https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/f20d0faf8761bd92da69a0386cf62d57.png"
        canonical={`${siteUrl}/contact`}
        jsonLd={contactJsonLd}
      />
      <Navbar />

      {/* Header */}
      <section className="relative pt-36 pb-20 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0">
          <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #0E1218 0%, #141F30 100%)' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <p className="text-accent text-xs font-semibold tracking-[3px] uppercase mb-3">Get in Touch</p>
          <h1 className="font-poppins font-bold text-4xl md:text-6xl text-white mb-4">Contact Us</h1>
          <p className="text-text-muted text-lg max-w-xl mx-auto leading-relaxed">
            Have questions or need guidance? Our team is ready to help you find the right animal health solutions.
          </p>
        </div>
      </section>

      <div className="section-divider" />

      {/* Main Content */}
      <section
        ref={ref as React.RefObject<HTMLElement>}
        className="py-16 px-4 md:px-8"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact Info Panel */}
          <div className={`lg:col-span-2 space-y-5 reveal ${isVisible ? 'visible' : ''}`}>
            <h2 className="font-poppins font-bold text-2xl text-white mb-6">Reach Out to Us</h2>

            {/* WhatsApp Card */}
            <a
              href="https://wa.me/923007321011"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 rounded-2xl border border-accent/30 hover:border-accent/60 transition-all duration-300 cursor-pointer group"
              style={{ background: '#131923' }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent/15 group-hover:bg-accent/25 transition-colors flex-shrink-0">
                <i className="ri-whatsapp-line text-accent text-2xl" />
              </div>
              <div>
                <p className="text-xs text-text-muted mb-0.5">WhatsApp (Preferred)</p>
                <p className="text-white font-semibold whitespace-nowrap">+92 300 7321011</p>
              </div>
              <i className="ri-arrow-right-up-line text-accent ml-auto" />
            </a>

            {/* Phone */}
            <div className="flex items-center gap-4 p-5 rounded-2xl border border-white/5" style={{ background: '#131923' }}>
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 flex-shrink-0">
                <i className="ri-phone-line text-accent text-xl" />
              </div>
              <div>
                <p className="text-xs text-text-muted mb-0.5">Phone</p>
                <a href="tel:+923007321011" className="text-white font-semibold hover:text-accent transition-colors cursor-pointer whitespace-nowrap">
                  +92 300 7321011
                </a>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start gap-4 p-5 rounded-2xl border border-white/5" style={{ background: '#131923' }}>
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 flex-shrink-0">
                <i className="ri-map-pin-2-line text-accent text-xl" />
              </div>
              <div>
                <p className="text-xs text-text-muted mb-0.5">Manufacturing Location</p>
                <p className="text-white font-semibold">Metsun Pharma</p>
                <p className="text-text-muted text-sm">8km Lahore GT Road, Rawat, Rawalpindi, Pakistan</p>
              </div>
            </div>

            {/* Hours */}
            <div className="flex items-center gap-4 p-5 rounded-2xl border border-white/5" style={{ background: '#131923' }}>
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 flex-shrink-0">
                <i className="ri-time-line text-accent text-xl" />
              </div>
              <div>
                <p className="text-xs text-text-muted mb-0.5">Business Hours</p>
                <p className="text-white font-semibold text-sm">Mon–Sat: 9:00 AM – 6:00 PM</p>
                <p className="text-text-muted text-xs">WhatsApp available anytime</p>
              </div>
            </div>
          </div>

          {/* Form Panel */}
          <div
            className={`lg:col-span-3 rounded-2xl p-8 border border-white/5 reveal reveal-delay-2 ${isVisible ? 'visible' : ''}`}
            style={{ background: '#131923' }}
          >
            <h2 className="font-poppins font-bold text-xl text-white mb-6">Send an Inquiry</h2>

            {formStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-500/15 mb-4">
                  <i className="ri-check-line text-green-400 text-3xl" />
                </div>
                <h3 className="font-poppins font-bold text-xl text-white mb-2">Inquiry Ready!</h3>
                <p className="text-text-muted text-sm max-w-xs">
                  Your WhatsApp chat has been opened with your inquiry details. If it didn&apos;t open automatically, check your browser&apos;s popup settings.
                </p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-6 text-accent text-sm font-medium hover:underline cursor-pointer whitespace-nowrap"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs text-text-muted mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your full name"
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-text-muted border border-white/8 focus:border-accent focus:outline-none transition-colors"
                      style={{ background: '#1A2130' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-text-muted mb-2">Phone / WhatsApp *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="+92 xxx xxxxxxx"
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-text-muted border border-white/8 focus:border-accent focus:outline-none transition-colors"
                      style={{ background: '#1A2130' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-text-muted mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-text-muted border border-white/8 focus:border-accent focus:outline-none transition-colors"
                    style={{ background: '#1A2130' }}
                  />
                </div>

                <div>
                  <label className="block text-xs text-text-muted mb-2">Inquiry Type</label>
                  <select
                    name="inquiry_type"
                    className="w-full px-4 py-3 rounded-xl text-sm text-white border border-white/8 focus:border-accent focus:outline-none transition-colors cursor-pointer"
                    style={{ background: '#1A2130' }}
                  >
                    <option value="product_inquiry">Product Inquiry</option>
                    <option value="distributor">Distributor Partnership</option>
                    <option value="bulk_order">Bulk Order</option>
                    <option value="veterinary_advice">Veterinary Advice</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-text-muted mb-2">
                    Message *
                    <span className={`ml-2 ${charCount > 480 ? 'text-accent' : ''}`}>
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your livestock and what you're looking for..."
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-text-muted border border-white/8 focus:border-accent focus:outline-none transition-colors resize-none"
                    style={{ background: '#1A2130' }}
                    onChange={(e) => setCharCount(e.target.value.length)}
                  />
                  {charCount > 500 && (
                    <p className="text-accent text-xs mt-1">Message cannot exceed 500 characters.</p>
                  )}
                </div>

                {formStatus === 'error' && (
                  <p className="text-red-400 text-sm">Something went wrong. Please try again or contact us via WhatsApp.</p>
                )}

                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={formStatus === 'sending' || charCount > 500}
                    className="wa-btn flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'sending' ? (
                      <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                    ) : (
                      <><i className="ri-send-plane-line" /> Send Inquiry</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
